import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { api } from '../../context/AuthContext';
import { 
  TrendingUp, TrendingDown, DollarSign, Building,
  Plus, RefreshCw, AlertCircle, Edit2, Trash2, MoreVertical
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const formatCurrency = (value, currency = 'EUR') => {
  if (!value) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

const formatPercent = (value) => {
  if (!value && value !== 0) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return `${num.toFixed(2)}%`;
};

const formatCompactDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const NavManagement = ({ projectId, v2ProjectId }) => {
  const [loading, setLoading] = useState(true);
  const [navData, setNavData] = useState(null);
  const [capexPreview, setCapexPreview] = useState(null);
  const [history, setHistory] = useState([]);
  
  // Modals
  const [showSetNav, setShowSetNav] = useState(false);
  const [showAddUpdate, setShowAddUpdate] = useState(false);
  const [showEditUpdate, setShowEditUpdate] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);
  
  // Forms
  const [navSource, setNavSource] = useState('capex');
  const [manualValue, setManualValue] = useState('');
  const [updateForm, setUpdateForm] = useState({
    nav_value: '',
    effective_date: new Date().toISOString().split('T')[0],
    notes: '',
    valuation_method: '',
    valuation_source: '',
  });

  const fetchNavData = useCallback(async () => {
    if (!v2ProjectId) return;
    
    try {
      setLoading(true);
      const [navRes, historyRes, capexRes] = await Promise.all([
        api.get(`/admin/projects/${v2ProjectId}/nav`),
        api.get(`/admin/projects/${v2ProjectId}/nav/history`),
        api.get(`/admin/projects/${v2ProjectId}/nav/capex-preview`),
      ]);
      
      setNavData(navRes.data);
      setHistory(historyRes.data.updates || []);
      setCapexPreview(capexRes.data);
    } catch (error) {
      console.error('Error fetching NAV data:', error);
      toast.error('Failed to load NAV data');
    } finally {
      setLoading(false);
    }
  }, [v2ProjectId]);

  useEffect(() => {
    fetchNavData();
  }, [fetchNavData]);

  const handleSetNav = async () => {
    try {
      await api.post(`/admin/projects/${v2ProjectId}/nav`, {
        nav_source: navSource,
        manual_value: navSource === 'manual' ? manualValue : null,
      });
      toast.success('Starting NAV set successfully');
      setShowSetNav(false);
      setManualValue('');
      fetchNavData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set NAV');
    }
  };

  const handleAddUpdate = async () => {
    try {
      await api.post(`/admin/projects/${v2ProjectId}/nav/update`, {
        nav_value: updateForm.nav_value,
        effective_date: new Date(updateForm.effective_date).toISOString(),
        notes: updateForm.notes || null,
        valuation_method: updateForm.valuation_method || null,
        valuation_source: updateForm.valuation_source || null,
      });
      toast.success('NAV update added');
      setShowAddUpdate(false);
      resetUpdateForm();
      fetchNavData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add NAV update');
    }
  };

  const handleEditUpdate = async () => {
    if (!editingUpdate) return;
    try {
      await api.put(`/admin/projects/${v2ProjectId}/nav/update/${editingUpdate.id}`, {
        nav_value: updateForm.nav_value,
        effective_date: new Date(updateForm.effective_date).toISOString(),
        notes: updateForm.notes || null,
        valuation_method: updateForm.valuation_method || null,
        valuation_source: updateForm.valuation_source || null,
      });
      toast.success('NAV update modified');
      setShowEditUpdate(false);
      setEditingUpdate(null);
      resetUpdateForm();
      fetchNavData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update NAV entry');
    }
  };

  const handleDeleteUpdate = async (updateId) => {
    if (!confirm('Delete this NAV entry? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/projects/${v2ProjectId}/nav/update/${updateId}`);
      toast.success('NAV entry deleted');
      fetchNavData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete NAV entry');
    }
  };

  const openEditModal = (update) => {
    setEditingUpdate(update);
    setUpdateForm({
      nav_value: update.nav_value,
      effective_date: update.effective_date.split('T')[0],
      notes: update.notes || '',
      valuation_method: update.valuation_method || '',
      valuation_source: update.valuation_source || '',
    });
    setShowEditUpdate(true);
  };

  const resetUpdateForm = () => {
    setUpdateForm({
      nav_value: '',
      effective_date: new Date().toISOString().split('T')[0],
      notes: '',
      valuation_method: '',
      valuation_source: '',
    });
  };

  if (loading) {
    return (
      <Card className="p-4 bg-white border-black/[0.04]">
        <div className="flex items-center justify-center py-6">
          <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      </Card>
    );
  }

  const hasStartingNav = navData?.starting_nav != null;
  const appreciation = parseFloat(navData?.total_appreciation_amount || 0);
  const appreciationPct = parseFloat(navData?.total_appreciation_percent || 0);

  return (
    <Card className="p-4 bg-white border-black/[0.04]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Building className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Net Asset Value (NAV)</h3>
        </div>
        <div className="flex gap-2">
          {hasStartingNav && (
            <Button variant="outline" size="sm" onClick={() => setShowAddUpdate(true)}>
              <Plus className="w-3 h-3 mr-1" />
              Update
            </Button>
          )}
          <Button 
            variant={hasStartingNav ? "ghost" : "default"} 
            size="sm"
            onClick={() => setShowSetNav(true)}
          >
            {hasStartingNav ? 'Configure' : 'Set NAV'}
          </Button>
        </div>
      </div>

      {!hasStartingNav ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Starting NAV Not Set</p>
              <p className="text-amber-600 text-xs mt-1">
                Set the starting NAV to track appreciation.
                {capexPreview?.has_capex_items && (
                  <span className="ml-1">CAPEX available: {formatCurrency(capexPreview.capex_total)}</span>
                )}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Compact NAV Summary */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="text-center p-2 bg-slate-50 rounded">
              <p className="text-[10px] text-gray-500 uppercase">Starting</p>
              <p className="font-semibold text-sm">{formatCurrency(navData.starting_nav)}</p>
            </div>
            <div className="text-center p-2 bg-slate-50 rounded">
              <p className="text-[10px] text-gray-500 uppercase">Current</p>
              <p className="font-semibold text-sm">{formatCurrency(navData.current_nav)}</p>
            </div>
            <div className="text-center p-2 bg-slate-50 rounded">
              <p className="text-[10px] text-gray-500 uppercase">Change</p>
              <p className={`font-semibold text-sm flex items-center justify-center gap-1 ${appreciation >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {appreciation >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {formatCurrency(Math.abs(appreciation))}
              </p>
            </div>
            <div className="text-center p-2 bg-slate-50 rounded">
              <p className="text-[10px] text-gray-500 uppercase">Return</p>
              <p className={`font-semibold text-sm ${appreciationPct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {appreciationPct >= 0 ? '+' : ''}{formatPercent(appreciationPct)}
              </p>
            </div>
          </div>

          {/* NAV History Table */}
          {history.length > 0 && (
            <div className="border border-black/[0.06] rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Date</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">NAV</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Change</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Notes</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((update, idx) => (
                    <tr key={update.id} className="border-t border-black/[0.04] hover:bg-slate-50/50">
                      <td className="py-2 px-3 text-gray-700">
                        {formatCompactDate(update.effective_date)}
                      </td>
                      <td className="py-2 px-3 text-right font-medium">
                        {formatCurrency(update.nav_value)}
                      </td>
                      <td className="py-2 px-3 text-right">
                        {update.change_amount ? (
                          <span className={parseFloat(update.change_amount) >= 0 ? 'text-emerald-600' : 'text-red-500'}>
                            {parseFloat(update.change_amount) >= 0 ? '+' : ''}{formatCurrency(update.change_amount)}
                            <span className="text-gray-400 ml-1">
                              ({parseFloat(update.change_percent) >= 0 ? '+' : ''}{formatPercent(update.change_percent)})
                            </span>
                          </span>
                        ) : (
                          <span className="text-gray-400">Initial</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-gray-500 truncate max-w-[150px]" title={update.notes}>
                        {update.notes || '-'}
                      </td>
                      <td className="py-2 px-1">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <MoreVertical className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditModal(update)}>
                              <Edit2 className="w-3 h-3 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {history.length > 1 && (
                              <DropdownMenuItem 
                                onClick={() => handleDeleteUpdate(update.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-3 h-3 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Set NAV Dialog */}
      <Dialog open={showSetNav} onOpenChange={setShowSetNav}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Starting NAV</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>NAV Source</Label>
              <Select value={navSource} onValueChange={setNavSource}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="capex">
                    From Budgeted CAPEX
                    {capexPreview?.has_capex_items && (
                      <span className="ml-2 text-gray-400">
                        ({formatCurrency(capexPreview.capex_total)})
                      </span>
                    )}
                  </SelectItem>
                  <SelectItem value="manual">Manual Input</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {navSource === 'capex' && !capexPreview?.has_capex_items && (
              <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                No CAPEX budget items found.
              </p>
            )}

            {navSource === 'manual' && (
              <div className="space-y-2">
                <Label>Starting NAV (EUR)</Label>
                <Input
                  type="number"
                  value={manualValue}
                  onChange={(e) => setManualValue(e.target.value)}
                  placeholder="e.g., 500000"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSetNav(false)}>Cancel</Button>
            <Button 
              onClick={handleSetNav}
              disabled={(navSource === 'capex' && !capexPreview?.has_capex_items) || 
                       (navSource === 'manual' && !manualValue)}
            >
              Set NAV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add NAV Update Dialog */}
      <Dialog open={showAddUpdate} onOpenChange={setShowAddUpdate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add NAV Update</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">NAV Value (EUR)</Label>
                <Input
                  type="number"
                  value={updateForm.nav_value}
                  onChange={(e) => setUpdateForm({...updateForm, nav_value: e.target.value})}
                  placeholder="550000"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Effective Date</Label>
                <Input
                  type="date"
                  value={updateForm.effective_date}
                  onChange={(e) => setUpdateForm({...updateForm, effective_date: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={updateForm.notes}
                onChange={(e) => setUpdateForm({...updateForm, notes: e.target.value})}
                placeholder="Q4 valuation..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowAddUpdate(false); resetUpdateForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleAddUpdate} disabled={!updateForm.nav_value}>
              Add Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit NAV Update Dialog */}
      <Dialog open={showEditUpdate} onOpenChange={(open) => { setShowEditUpdate(open); if (!open) setEditingUpdate(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit NAV Entry</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">NAV Value (EUR)</Label>
                <Input
                  type="number"
                  value={updateForm.nav_value}
                  onChange={(e) => setUpdateForm({...updateForm, nav_value: e.target.value})}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Effective Date</Label>
                <Input
                  type="date"
                  value={updateForm.effective_date}
                  onChange={(e) => setUpdateForm({...updateForm, effective_date: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={updateForm.notes}
                onChange={(e) => setUpdateForm({...updateForm, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowEditUpdate(false); setEditingUpdate(null); resetUpdateForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleEditUpdate} disabled={!updateForm.nav_value}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default NavManagement;
